// src/components/OrderCard.web.js - Fixed to prevent form control circles
import React, { useState } from 'react';
import { View, StyleSheet, Modal, TouchableOpacity, ScrollView, Text, Platform } from 'react-native';
import { Card, Title, Paragraph, Button, Chip, Divider } from 'react-native-paper';

const OrderCardWeb = ({ 
  order, 
  onPress, 
  userType, // 'זכיין' or other user types
  franchiseeBranches = [], // Array of branches
  onLinkToBranch, // Callback function when linking order to branch
  isLinked = false // Whether order is already linked to a branch
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'הצעת מחיר':
        return '#ff9800';
      case 'אושר':
        return '#4caf50';
      case 'נדחה':
        return '#f44336';
      default:
        return '#2196f3';
    }
  };

  const handleBranchSelection = (branchId, branchName) => {
    console.log('🔗 Branch selection:', { branchId, branchName, orderId: order?.id });
    try {
      setModalVisible(false);
      if (onLinkToBranch && branchId && branchName && order?.id) {
        console.log('✅ Calling onLinkToBranch callback');
        onLinkToBranch(order.id, branchId, branchName);
      } else {
        console.error('❌ Missing data for branch linking:', { 
          hasCallback: !!onLinkToBranch,
          branchId, 
          branchName, 
          orderId: order?.id 
        });
      }
    } catch (error) {
      console.error('❌ Error in handleBranchSelection:', error);
    }
  };

  const handleModalToggle = () => {
    try {
      console.log('🖱️ Branch button pressed, opening modal');
      setModalVisible(true);
    } catch (error) {
      console.error('❌ Error opening modal:', error);
    }
  };

  const showFranchiseeOptions = userType === 'זכיין' && 
    Array.isArray(franchiseeBranches) && 
    franchiseeBranches.length > 0 &&
    Boolean(order?.id);

  // Safety check - don't render if order is missing required data
  if (!order || !order.orderNumber) {
    return null;
  }

  return (
    <>
      <Card style={styles.card} onPress={onPress}>
        <Card.Content>
          <View style={styles.header}>
            <View style={styles.orderInfo}>
              <Title style={styles.orderNumber}>{order?.orderNumber || 'N/A'}</Title>
              <Paragraph style={styles.organization}>{order?.organizationName || ''}</Paragraph>
            </View>
            <Chip 
              mode="flat" 
              style={[styles.statusChip, { backgroundColor: getStatusColor(order?.status) + '20' }]}
              textStyle={[styles.statusText, { color: getStatusColor(order?.status) }]}
            >
              {order?.status || 'לא ידוע'}
            </Chip>
          </View>

          <View style={styles.details}>
            <View style={styles.detailRow}>
              <Text style={styles.icon}>🚚</Text>
              <Paragraph style={styles.detailText}>{order?.pickUpType || ''}</Paragraph>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.icon}>🏪</Text>
              <Paragraph style={styles.detailText}>
                {(() => {
                  const branchName = `${order?.branchName || order?.name || 'סניף ללא שם'} `;

                  if (!branchName || branchName === 'לא נבחר') {
                    return 'לא שוייך לסניף';
                  }
                  
                  // Find the branch in franchiseeBranches to get fullAddress
                  const linkedBranch = franchiseeBranches.find(branch => 
                    branch.id === order?.branchId || branch.id === order?.linkedToBranchId
                  );
                  
                  if (linkedBranch?.fullAddress) {
                    // Remove "ישראל" from fullAddress
                    const cleanAddress = linkedBranch.fullAddress.replace(/,?\s*ישראל\s*,?/gi, '').trim();
                    return `${branchName} - ${cleanAddress}`;
                  } else {
                    return branchName;
                  }
                })()}
              </Paragraph>
              {isLinked && (
                <Text style={[styles.icon, styles.linkedIcon]}>🔗</Text>
              )}
            </View>
            
            <View style={styles.detailRow}>
              <Text style={styles.icon}>📅</Text>
              <Paragraph style={styles.detailText}>
                {order?.customerOrderDate ? formatDate(order.customerOrderDate) : ''}
              </Paragraph>
            </View>

            <View style={styles.detailRow}>
              <Text style={[styles.icon, styles.priceIcon]}>₪</Text>
              <Paragraph style={[styles.detailText, styles.price]}>
                {(order?.totalAmount || 0) - (order?.totalProfit || 0)}
              </Paragraph>
            </View>
          </View>

          <View style={styles.buttonContainer}>
            <Button 
              mode="contained" 
              onPress={onPress}
              style={[styles.viewButton, showFranchiseeOptions && styles.primaryButton]}
              contentStyle={styles.buttonContent}
              labelStyle={styles.buttonLabel}
            >
              הצג הזמנה
            </Button>

            {showFranchiseeOptions && (
              <Button
                mode={isLinked ? "contained" : "outlined"}
                onPress={handleModalToggle}
                style={[
                  styles.branchButton,
                  isLinked && styles.linkedButton
                ]}
                contentStyle={styles.buttonContent}
                labelStyle={[
                  styles.branchButtonLabel,
                  isLinked && styles.linkedButtonLabel
                ]}
                icon={() => (
                  <Text style={styles.buttonIcon}>
                    {isLinked ? "🔗" : "🏪"}
                  </Text>
                )}
              >
                {isLinked ? "מקושר לסניף" : "קשר לסניף"}
              </Button>
            )}
          </View>
        </Card.Content>
      </Card>

      {/* Branch Selection Modal */}
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Title style={styles.modalTitle}>בחר סניף לקישור</Title>
              <TouchableOpacity 
                onPress={() => setModalVisible(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeIcon}>❌</Text>
              </TouchableOpacity>
            </View>
            
            <Divider />
            
            <ScrollView style={styles.branchList}>
              {Array.isArray(franchiseeBranches) && franchiseeBranches.map((branch, index) => {
                if (!branch || !branch.id) {
                  console.warn('⚠️ Invalid branch data:', branch);
                  return null;
                }
                
                const branchDisplayName = `${branch.branchName || branch.name || 'סניף ללא שם'} - ${branch.streetName || 'כתובת לא זמינה'}`;
                
                return (
                  <TouchableOpacity
                    key={`${branch.id}-${index}`}
                    style={styles.branchItem}
                    onPress={() => handleBranchSelection(branch.id, branchDisplayName)}
                  >
                    <View style={styles.branchItemContent}>
                      <Text style={styles.icon}>🏪</Text>
                      <Paragraph style={styles.branchItemText}>{branchDisplayName}</Paragraph>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 12,
    elevation: 3,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  orderInfo: {
    flex: 1,
  },
  orderNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  organization: {
    fontSize: 14,
    color: '#666',
  },
  statusChip: {
    borderRadius: 16,
  },
  statusText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  details: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  icon: {
    fontSize: 16,
    width: 20,
    textAlign: 'center',
    marginRight: 8,
    color: '#666',
  },
  linkedIcon: {
    color: '#4CAF50',
    marginLeft: 8,
    marginRight: 0,
  },
  priceIcon: {
    color: '#4CAF50',
    fontSize: 14,
    fontWeight: 'bold',
  },
  detailText: {
    fontSize: 14,
    color: '#666',
    flex: 1,
  },
  price: {
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  viewButton: {
    flex: 1,
    borderRadius: 8,
    backgroundColor: '#2196F3',
  },
  primaryButton: {
    flex: 1,
  },
  branchButton: {
    borderRadius: 8,
    borderColor: '#FF6B35',
    backgroundColor: 'transparent',
  },
  linkedButton: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  buttonContent: {
    height: 40,
  },
  buttonLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#fff',
  },
  branchButtonLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FF6B35',
  },
  linkedButtonLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#fff',
  },
  buttonIcon: {
    fontSize: 14,
    textAlign: 'center',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    margin: 20,
    maxHeight: '80%',
    width: '90%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    padding: 4,
  },
  closeIcon: {
    fontSize: 20,
    color: '#666',
  },
  branchList: {
    maxHeight: 300,
  },
  branchItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  branchItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  branchItemText: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
});

export default OrderCardWeb;